import React, { useState, useEffect, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Grid,
  Box,
  Typography,
  Chip
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';

export default function ExactLocation({ formData, setFormData }) {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);

  const autocompleteService = useMemo(() => {
    if (window?.google?.maps?.places) {
      return new window.google.maps.places.AutocompleteService();
    }
    return null;
  }, []);

  const placesService = useMemo(() => {
    if (window?.google?.maps?.places) {
      return new window.google.maps.places.PlacesService(document.createElement('div'));
    }
    return null;
  }, []);

  const fetchPredictions = useMemo(
    () =>
      debounce((input, callback) => {
        if (autocompleteService) {
          autocompleteService.getPlacePredictions({ input }, callback);
        }
      }, 400),
    [autocompleteService]
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService || inputValue === '') {
      setOptions(value ? [value] : []);
      return;
    }

    fetchPredictions(inputValue, (results) => {
      if (active) {
        let newOptions = value ? [value] : [];
        if (results) newOptions = [...newOptions, ...results];
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, value, fetchPredictions, autocompleteService]);

  const fetchPlaceDetails = (placeId) => {
    if (!placesService) return;
    placesService.getDetails({ placeId }, (place) => {
      const location = place.formatted_address;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setFormData({
        ...formData,
        location,
        lat,
        lng
      });
    });
  };

  return (
    <Autocomplete
      id="google-map-autocomplete"
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.description
      }
      filterOptions={(x) => x}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="No locations"
      onChange={(event, newValue) => {
        setOptions(newValue ? [newValue, ...options] : []);
        setValue(newValue);

        if (newValue?.place_id) {
          fetchPlaceDetails(newValue.place_id);
        }
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField {...params} label="E - Exact Location" fullWidth />
      )}
      renderOption={(props, option) => {
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];

        const parts = parse(
          option.structured_formatting.main_text,
          matches.map((match) => [match.offset, match.offset + match.length])
        );

        return (
          <li {...props}>
            <Grid container alignItems="center">
              <Grid item sx={{ display: 'flex', width: 44 }}>
                <LocationOnIcon sx={{ color: 'text.secondary' }} />
              </Grid>
              <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
}
